import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Grid,
  Row,
  Select,
  Table,
  Typography,
  message,
  Space,
  Divider,
} from "antd";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { FaArrowRight, FaCertificate, FaHotel, FaTable } from "react-icons/fa";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type Props = {
  onNavigate: (viewKey: "orders" | "ecertificates" | "hotelBookings") => void;
};

// ✅ adjust if your Firestore collection names differ
const ECERT_COLLECTION = "ecertificate_requests";
const HOTEL_BOOKINGS_COLLECTION = "hotelBookings";

type ContactRow = {
  organization?: string;
  status?: string;
  country?: string;
  nationality?: string;
};

type HotelBookingRow = {
  status?: string; // optional (if you have it)
};

type ECertRow = {
  status?: string; // optional (if you have it)
};

const glassCard: React.CSSProperties = {
  borderRadius: 18,
  border: "1px solid rgba(15, 23, 42, 0.06)",
  background: "rgba(255,255,255,0.92)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
};

const sectionCard: React.CSSProperties = {
  ...glassCard,
  overflow: "hidden",
};

const sectionHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const statTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: "rgba(15,23,42,0.55)",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
};

const statValue: React.CSSProperties = {
  marginTop: 6,
  fontSize: 28,
  fontWeight: 900,
  color: "rgba(15,23,42,0.92)",
  lineHeight: 1,
};

const statHint: React.CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: "rgba(15,23,42,0.55)",
};

const barTrack: React.CSSProperties = {
  height: 10,
  borderRadius: 999,
  background: "rgba(15,23,42,0.08)",
  overflow: "hidden",
};

const barFill = (pct: number): React.CSSProperties => ({
  height: "100%",
  width: `${pct}%`,
  borderRadius: 999,
  background: "linear-gradient(90deg, rgba(47,84,235,1), rgba(47,84,235,0.55))",
});

const normalizeCompany = (s?: string) =>
  (s || "").trim().replace(/\s+/g, " ").toLowerCase();

const displayCompany = (s?: string) => (s || "N/A").trim() || "N/A";

const shouldCountCompany = (org?: string) => {
  const n = normalizeCompany(org);
  if (!n) return false;
  if (n === "n/a" || n === "na" || n === "-" || n === "none") return false;
  return true;
};

const pct = (n: number, total: number) =>
  total === 0 ? 0 : Math.round((n / total) * 100);

const isPaid = (status?: string) => (status || "").toLowerCase() === "paid";
const isManual = (status?: string) =>
  (status || "").toLowerCase() === "manual entry";

const DashboardStats: React.FC<Props> = ({ onNavigate }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  // Loading flags per module (so one failing won’t kill all)
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [loadingHotel, setLoadingHotel] = useState(false);
  const [loadingEcert, setLoadingEcert] = useState(false);

  // Registrations data
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState<string>("");

  // Hotel data
  const [hotelBookings, setHotelBookings] = useState<HotelBookingRow[]>([]);

  // E-cert data
  const [ecerts, setEcerts] = useState<ECertRow[]>([]);

  // prevent overlap + setState after unmount
  const mountedRef = useRef(true);
  const inFlightRegs = useRef(false);
  const inFlightHotel = useRef(false);
  const inFlightEcert = useRef(false);

  const [hasLoadedRegs, setHasLoadedRegs] = useState(false);
  const [reloadingRegs, setReloadingRegs] = useState(false);

  // ✅ Registrations fetch (orders -> contacts)
  const fetchRegistrationsStats = async () => {
    if (inFlightRegs.current) return;
    inFlightRegs.current = true;

    // ✅ First time: use Card skeleton
    // ✅ Subsequent refresh: keep data, show small spinner
    if (!hasLoadedRegs) setLoadingRegs(true);
    else setReloadingRegs(true);

    try {
      const ordersSnap = await getDocs(collection(db, "orders"));

      // OPTIONAL but recommended: parallelize for speed
      const contactSnaps = await Promise.all(
        ordersSnap.docs.map((orderDoc) =>
          getDocs(collection(db, `orders/${orderDoc.id}/contacts`)),
        ),
      );

      const allContacts: ContactRow[] = [];
      contactSnaps.forEach((contactsSnap) => {
        contactsSnap.docs.forEach((d) => {
          const data = d.data() as any;
          allContacts.push({
            organization: data.organization || "N/A",
            status: data.status || "Awaiting Payment Confirmation",
            country: data.country || "N/A",
            nationality: data.nationality || "N/A",
          });
        });
      });

      if (!mountedRef.current) return;
      setContacts(allContacts);
      setHasLoadedRegs(true);
    } catch (e) {
      console.error("❌ Registrations stats fetch failed:", e);
      if (!mountedRef.current) return;
      message.error("Failed to load Registrations stats.");
    } finally {
      inFlightRegs.current = false;
      if (!mountedRef.current) return;

      setLoadingRegs(false);
      setReloadingRegs(false);
    }
  };

  // ✅ Hotel bookings fetch (simple collection read)
  const fetchHotelStats = async () => {
    if (inFlightHotel.current) return;
    inFlightHotel.current = true;
    setLoadingHotel(true);

    try {
      const snap = await getDocs(collection(db, HOTEL_BOOKINGS_COLLECTION));
      const all = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          status: data.status || "N/A",
        };
      });

      if (!mountedRef.current) return;
      setHotelBookings(all);
    } catch (e) {
      console.error("❌ Hotel stats fetch failed:", e);
      if (!mountedRef.current) return;
      setHotelBookings([]);
      message.error("Failed to load Hotel Bookings stats.");
    } finally {
      if (mountedRef.current) setLoadingHotel(false);
      inFlightHotel.current = false;
    }
  };

  // ✅ E-cert fetch (simple collection read)
  const fetchEcertStats = async () => {
    if (inFlightEcert.current) return;
    inFlightEcert.current = true;
    setLoadingEcert(true);

    try {
      const snap = await getDocs(collection(db, ECERT_COLLECTION));
      const all = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          status: data.status || "N/A",
        };
      });

      if (!mountedRef.current) return;
      setEcerts(all);
    } catch (e) {
      console.error("❌ E-cert stats fetch failed:", e);
      if (!mountedRef.current) return;
      setEcerts([]);
      message.error("Failed to load E-Cert stats.");
    } finally {
      if (mountedRef.current) setLoadingEcert(false);
      inFlightEcert.current = false;
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    // initial
    fetchRegistrationsStats();
    fetchHotelStats();
    fetchEcertStats();

    // refresh
    const id = setInterval(() => {
      fetchRegistrationsStats();
      fetchHotelStats();
      fetchEcertStats();
    }, 500000);

    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  // ---------------------------
  // Registrations computed stats
  // ---------------------------
  const regStats = useMemo(() => {
    const totalRegistrations = contacts.length;

    const paidCount = contacts.filter(
      (c) => !isManual(c.status) && isPaid(c.status),
    ).length;

    const pendingUnpaidCount = contacts.filter((c) => {
      if (isManual(c.status)) return false;
      return !isPaid(c.status);
    }).length;

    const uniqueCompanies = new Set(
      contacts
        .filter((c) => !isManual(c.status))
        .map((c) => normalizeCompany(c.organization))
        .filter((k) => shouldCountCompany(k)),
    ).size;

    const companyCounts: Record<
      string,
      { key: string; label: string; count: number }
    > = {};

    contacts
      .filter((c) => !isManual(c.status))
      .forEach((c) => {
        if (!shouldCountCompany(c.organization)) return;
        const key = normalizeCompany(c.organization);
        const label = displayCompany(c.organization);

        if (!companyCounts[key]) companyCounts[key] = { key, label, count: 0 };
        companyCounts[key].count += 1;
      });

    const companies = Object.values(companyCounts).sort(
      (a, b) => b.count - a.count,
    );

    const joinedInSelectedCompany = selectedCompanyKey
      ? (companyCounts[selectedCompanyKey]?.count ?? 0)
      : 0;

    return {
      totalRegistrations,
      paidCount,
      pendingUnpaidCount,
      uniqueCompanies,
      companies,
      joinedInSelectedCompany,
      companyKeys: new Set(Object.keys(companyCounts)),
      payment: {
        paid: paidCount,
        pending: pendingUnpaidCount,
        total: totalRegistrations,
        paidPct: pct(paidCount, totalRegistrations),
        pendingPct: pct(pendingUnpaidCount, totalRegistrations),
      },
    };
  }, [contacts, selectedCompanyKey]);

  useEffect(() => {
    if (selectedCompanyKey && !regStats.companyKeys.has(selectedCompanyKey)) {
      setSelectedCompanyKey("");
    }
  }, [selectedCompanyKey, regStats.companyKeys]);

  const companyOptions = useMemo(
    () =>
      regStats.companies.map((c) => ({
        value: c.key,
        label: `${c.label}`,
      })),
    [regStats.companies],
  );

  const topCompanyRows = useMemo(
    () =>
      regStats.companies.slice(0, 10).map((c) => ({
        key: c.key,
        company: c.label,
        joined: c.count,
      })),
    [regStats.companies],
  );

  // ---------------------------
  // Hotel computed stats
  // ---------------------------
  const hotelStats = useMemo(() => {
    const total = hotelBookings.length;
    // If you have real statuses, you can split them here. For now: show total only.
    const pending = hotelBookings.filter((b) =>
      (b.status || "").toLowerCase().includes("pending"),
    ).length;
    const confirmed = hotelBookings.filter((b) =>
      (b.status || "").toLowerCase().includes("confirm"),
    ).length;

    return { total, pending, confirmed };
  }, [hotelBookings]);

  // ---------------------------
  // E-cert computed stats
  // ---------------------------
  const ecertStats = useMemo(() => {
    const total = ecerts.length;
    const pending = ecerts.filter((e) =>
      (e.status || "").toLowerCase().includes("pending"),
    ).length;
    const approved = ecerts.filter((e) =>
      (e.status || "").toLowerCase().includes("approve"),
    ).length;

    return { total, pending, approved };
  }, [ecerts]);

  const companyAgg = useMemo(() => {
    const map: Record<
      string,
      {
        key: string;
        label: string;
        registrations: number;
        paid: number;
        companions: number;
        paidPct: number;
      }
    > = {};

    contacts
      .filter((c) => !isManual(c.status)) // exclude manual entries (consistent)
      .forEach((c) => {
        if (!shouldCountCompany(c.organization)) return;

        const key = normalizeCompany(c.organization);
        const label = displayCompany(c.organization);

        if (!map[key]) {
          map[key] = {
            key,
            label,
            registrations: 0,
            paid: 0,
            companions: 0,
            paidPct: 0,
          };
        }

        map[key].registrations += 1;

        if (isPaid(c.status)) map[key].paid += 1;
      });

    const rows = Object.values(map).map((r) => ({
      ...r,
      paidPct: r.registrations
        ? Math.round((r.paid / r.registrations) * 100)
        : 0,
    }));

    // sort: most registrations first (you can change to lowest paidPct if you prefer)
    rows.sort((a, b) => b.registrations - a.registrations);

    return rows;
  }, [contacts]);
  const [geoMode, setGeoMode] = useState<"country" | "nationality">("country");

  const [paidChartFilter, setPaidChartFilter] = useState<
    "all" | "fullyPaid" | "notFullyPaid" | "unpaid" | "hasCompanion"
  >("all");

  const [paidChartSort, setPaidChartSort] = useState<
    "lowestPaidPct" | "highestPaidPct" | "mostRegistrations"
  >("lowestPaidPct");

  const paidChartFilterOptions = useMemo(
    () => [
      { value: "all", label: "All companies" },
      { value: "notFullyPaid", label: "Not fully paid" },
      { value: "fullyPaid", label: "Fully paid (100%)" },
      { value: "unpaid", label: "Unpaid (0%)" },
      { value: "hasCompanion", label: "Has companions" },
    ],
    [],
  );

  const paidChartSortOptions = useMemo(
    () => [
      { value: "lowestPaidPct", label: "Lowest paid %" },
      { value: "highestPaidPct", label: "Highest paid %" },
      { value: "mostRegistrations", label: "Most registrations" },
    ],
    [],
  );

  const paidCompanyChartRows = useMemo(() => {
    let rows = [...companyAgg];

    // filter
    rows = rows.filter((c) => {
      if (paidChartFilter === "all") return true;
      if (paidChartFilter === "fullyPaid") return c.paidPct === 100;
      if (paidChartFilter === "notFullyPaid") return c.paidPct < 100;
      if (paidChartFilter === "unpaid") return c.paidPct === 0;
      if (paidChartFilter === "hasCompanion") return (c.companions || 0) > 0;
      return true;
    });

    // sort
    rows.sort((a, b) => {
      if (paidChartSort === "mostRegistrations")
        return b.registrations - a.registrations;
      if (paidChartSort === "highestPaidPct") return b.paidPct - a.paidPct;
      return a.paidPct - b.paidPct; // lowestPaidPct default
    });

    return rows.map((c) => ({
      key: c.key,
      label: c.label,
      pct: c.paidPct,
      paid: c.paid,
      total: c.registrations,
      companions: c.companions,
    }));
  }, [companyAgg, paidChartFilter, paidChartSort]);

  const normalizeGeo = (s?: string) =>
    (s || "").trim().replace(/\s+/g, " ").toLowerCase();

  const displayGeo = (s?: string) => (s || "N/A").trim() || "N/A";

  const shouldCountGeo = (s?: string) => {
    const n = normalizeGeo(s);
    if (!n) return false;
    if (n === "n/a" || n === "na" || n === "-" || n === "none") return false;
    return true;
  };
  const topGeoRows = useMemo(() => {
    const keyField = geoMode; // "country" | "nationality"

    const counts: Record<
      string,
      { key: string; label: string; count: number }
    > = {};

    // ✅ INCLUDE MANUAL TOO: no status filter here
    contacts.forEach((c: any) => {
      const raw = c?.[keyField];
      if (!shouldCountGeo(raw)) return;

      const key = normalizeGeo(raw);
      const label = displayGeo(raw);

      if (!counts[key]) counts[key] = { key, label, count: 0 };
      counts[key].count += 1;
    });

    return Object.values(counts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((x) => ({
        key: x.key,
        geo: x.label,
        joined: x.count,
      }));
  }, [contacts, geoMode]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* =========================
          1) REGISTRATIONS SECTION
         ========================= */}
      <Card
        style={sectionCard}
        bodyStyle={{ padding: 16 }}
        loading={loadingRegs}
      >
        <div style={sectionHeader}>
          <div>
            <Title
              level={5}
              style={{
                margin: 0, // ✅ remove default spacing
                lineHeight: 1.1,
              }}
            >
              REGISTRATIONS
            </Title>

            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              Overview of participants, payments, and companies
            </Text>
          </div>

          <Button
            type="primary"
            onClick={() => onNavigate("orders")}
            style={{ borderRadius: 12 }}
            // icon={<FaArrowRight />}
          >
            Go to Registrations
          </Button>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Row gutter={[14, 14]} align="stretch">
          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Total Registrations</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {regStats.totalRegistrations}
              </div>
              <div style={statHint}>All participants in the system</div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>TOTAL PARTICIPATING COMPANIES</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {regStats.uniqueCompanies}
              </div>
              <div style={statHint}>Based on organization</div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={statTitle}>Delegates per Company</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select company"
                  style={{ width: isMobile ? "100%" : 320 }}
                  options={companyOptions}
                  value={selectedCompanyKey || undefined}
                  onChange={(val) => setSelectedCompanyKey(val || "")}
                  filterOption={(input, option) =>
                    String(option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </div>

              <div style={{ ...statValue }}>
                {regStats.joinedInSelectedCompany}
              </div>

              <div style={{ marginTop: 8 }}>
                {selectedCompanyKey ? (
                  <div style={statHint}>
                    Count of registrations under selected company
                  </div>
                ) : (
                  <Text type="secondary" style={statHint}>
                    No company selected
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[14, 14]} align="stretch" style={{ marginTop: 14 }}>
          {/* LEFT: graphs */}
          <Col xs={24} lg={8} style={{ display: "flex" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                width: "100%",
              }}
            >
              {/* Payment Breakdown (your donut card) */}
              <Card
                style={glassCard}
                bodyStyle={{ padding: 16 }}
                bordered={false}
              >
                {/* ✅ make donut bigger */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Payment Breakdown
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Paid vs Pending/Unpaid (manual entries excluded)
                    </Text>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    marginTop: 14,
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: 125,
                      height: 125,
                      borderRadius: "50%",
                      background: `conic-gradient(
                rgba(47,84,235,1) 0% ${regStats.payment.paidPct}%,
                rgba(15,23,42,0.10) ${regStats.payment.paidPct}% 100%
              )`,
                      position: "relative",
                      flex: "0 0 auto",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 22,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <div style={{ fontSize: 26, fontWeight: 900 }}>
                        {regStats.payment.paidPct}%
                      </div>
                      <div
                        style={{ fontSize: 12, color: "rgba(15,23,42,0.55)" }}
                      >
                        Paid
                      </div>
                    </div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 10,
                      }}
                    >
                      <Text strong>Paid</Text>
                      <Text>
                        {regStats.payment.paid} / {regStats.payment.total}
                      </Text>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text strong>Pending / Unpaid</Text>
                      <Text>
                        {regStats.payment.pending} / {regStats.payment.total}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>

              {/* ✅ NEW: Delegates per company (bar chart) */}
              <Card
                style={glassCard}
                bodyStyle={{ padding: 16 }}
                bordered={false}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Paid per Company
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Each bar max is 100% (paid / registrations)
                    </Text>
                  </div>

                  <Space wrap>
                    <Select
                      value={paidChartFilter}
                      onChange={setPaidChartFilter}
                      options={paidChartFilterOptions}
                      style={{ width: isMobile ? "100%" : 190 }}
                    />
                    <Select
                      value={paidChartSort}
                      onChange={setPaidChartSort}
                      options={paidChartSortOptions}
                      style={{ width: isMobile ? "100%" : 170 }}
                    />
                  </Space>
                </div>

                <Divider style={{ margin: "12px 0" }} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    maxHeight: isMobile ? 200 : 260, // 👈 adjust as you like
                    overflowY: "auto",
                    paddingRight: 6, // avoids scrollbar overlap
                  }}
                >
                  {paidCompanyChartRows.map((r) => (
                    <div
                      key={r.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 110px",
                        gap: 10,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "rgba(15,23,42,0.85)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={r.label}
                        >
                          {r.label}
                        </div>

                        <div style={barTrack}>
                          <div style={barFill(r.pct)} />
                        </div>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 900 }}>{r.pct}%</div>
                        <div
                          style={{ fontSize: 12, color: "rgba(15,23,42,0.55)" }}
                        >
                          {r.paid}/{r.total} paid
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </Col>

          {/* RIGHT: table */}
          <Col xs={24} lg={8} style={{ display: "flex" }}>
            <Card
              style={{ ...glassCard, width: "100%" }}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={{ marginBottom: 10 }}>
                <Title level={5} style={{ margin: 0 }}>
                  Top 10 Companies
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Companies with most number of attendees
                </Text>
              </div>

              <Table
                size="small"
                pagination={false}
                dataSource={topCompanyRows}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No data"
                    />
                  ),
                }}
                columns={[
                  {
                    title: "Company",
                    dataIndex: "company",
                    key: "company",
                    ellipsis: true,
                  },
                  {
                    title: "Delegates",
                    dataIndex: "joined",
                    key: "joined",
                    width: 90,
                    align: "right",
                  },
                ]}
                style={{ background: "transparent" }}
                scroll={{ y: isMobile ? 320 : 420 }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8} style={{ display: "flex" }}>
            <Card
              style={{ ...glassCard, width: "100%" }}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    Top 10{" "}
                    {geoMode === "country" ? "Countries" : "Nationalities"}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Based on participant profile
                  </Text>
                </div>

                <Select
                  value={geoMode}
                  onChange={(v) => setGeoMode(v)}
                  style={{ width: 160 }}
                  options={[
                    { value: "country", label: "Country" },
                    { value: "nationality", label: "Nationality" },
                  ]}
                />
              </div>

              <Table
                size="small"
                pagination={false}
                dataSource={topGeoRows}
                columns={[
                  {
                    title: geoMode === "country" ? "Country" : "Nationality",
                    dataIndex: "geo",
                    key: "geo",
                    ellipsis: true,
                  },
                  {
                    title: "Delegates",
                    dataIndex: "joined",
                    key: "joined",
                    width: 90,
                    align: "right",
                  },
                ]}
                style={{ background: "transparent" }}
                scroll={{ y: isMobile ? 320 : 420 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No data"
                    />
                  ),
                }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* =========================
          2) HOTEL BOOKINGS SECTION
         ========================= */}
      <Card
        style={sectionCard}
        bodyStyle={{ padding: 16 }}
        loading={loadingHotel}
      >
        <div style={sectionHeader}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Hotel Bookings
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Overview of hotel bookings
            </Text>
          </div>

          <Button
            type="primary"
            onClick={() => onNavigate("hotelBookings")}
            style={{ borderRadius: 12 }}
            // icon={<FaArrowRight />}
          >
            Go to Hotel Bookings
          </Button>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Row gutter={[14, 14]} align="stretch">
          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Total Bookings</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {hotelStats.total}
              </div>
              <div style={statHint}>All bookings in the system</div>
            </Card>
          </Col>

          {/* <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Confirmed</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {hotelStats.confirmed}
              </div>
              <div style={statHint}>Confirmed booking status</div>
            </Card>
          </Col> */}

          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Pending</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {hotelStats.pending}
              </div>
              <div style={statHint}>Pending Booking Status</div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* =========================
          3) E-CERT REQUESTS SECTION
         ========================= */}
      <Card
        style={sectionCard}
        bodyStyle={{ padding: 16 }}
        loading={loadingEcert}
      >
        <div style={sectionHeader}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              E-Certificate Requests
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Overview of certificate requests
            </Text>
          </div>

          <Button
            type="primary"
            onClick={() => onNavigate("ecertificates")}
            style={{ borderRadius: 12 }}
            // icon={<FaArrowRight />}
          >
            Go to E-Certs
          </Button>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Row gutter={[14, 14]} align="stretch">
          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Total Requests</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {ecertStats.total}
              </div>
              <div style={statHint}>All requests in the system</div>
            </Card>
          </Col>

          {/* <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Approved</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {ecertStats.approved}
              </div>
              <div style={statHint}>If status contains “approved”</div>
            </Card>
          </Col> */}

          <Col xs={24} lg={8}>
            <Card
              style={glassCard}
              bodyStyle={{ padding: 16 }}
              bordered={false}
            >
              <div style={statTitle}>Pending</div>
              <div style={{ ...statValue, marginTop: 20 }}>
                {ecertStats.pending}
              </div>
              <div style={statHint}>Pending Requests</div>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DashboardStats;
